import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { RecipeResult } from "@/components/RecipeResult";
import AuthModal from "@/components/AuthModal";
import { CategorySection } from "@/components/recipe/CategorySection";
import { LoadingOverlay } from "@/components/recipe/LoadingOverlay";
import { UserMenu } from "@/components/recipe/UserMenu";
import { useAuthUser } from "@/hooks/useAuthUser";
import { useCategoryOptions } from "@/hooks/useCategoryOptions";
import { useRecipeMutation } from "@/hooks/useRecipeMutation";
import { useRecipeSelection } from "@/hooks/useRecipeSelection";
import { isSupabaseConfigured, supabase } from "@/lib/supabaseClient";
import { ChefHat, Minus, Plus } from "lucide-react";

export const RecipePreferenceForm = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuthUser();
  const {
    selected,
    toggleSelection,
    toggleIngredient,
    setPortions,
    setPreferences,
    removeFromSelected,
  } = useRecipeSelection();
  const catalog = useCategoryOptions(removeFromSelected);

  const [loadingMessage, setLoadingMessage] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const mutation = useRecipeMutation(() => setLoadingMessage(""));

  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL ?? "[email address]";

  const submitRecipe = () => {
    if (isSupabaseConfigured && !user) {
      setShowAuthModal(true);
      return;
    }
    setLoadingMessage("Preparing your meal");
    mutation.mutate(selected);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitRecipe();
  };

  const handleSignOut = async () => {
    if (!isSupabaseConfigured || !supabase) {
      toast({
        title: "Authentication unavailable",
        description: "Supabase is not configured for this deployment.",
        variant: "destructive",
      });
      return;
    }

    setLoadingMessage("Logging off");
    try {
      await supabase.auth.signOut();
      setUser(null);
      mutation.reset();
      navigate("/");
      toast({ title: "Signed out", description: "You have been signed out." });
    } catch (err: any) {
      toast({
        title: "Sign out error",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    } finally {
      setLoadingMessage("");
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 md:p-12">
      <div className="mx-auto max-w-4xl">
        <div className="flex justify-end mb-4">
          <UserMenu
            user={user}
            onLogin={() => setShowAuthModal(true)}
            onSignOut={handleSignOut}
            onPreferences={() =>
              toast({
                title: "Preferences",
                description: "Preferences page not implemented yet.",
              })
            }
          />
        </div>

        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center justify-center rounded-full bg-primary/10 p-4">
            <ChefHat className="h-8 w-8 text-primary" />
          </div>
          <h1 className="mb-2 text-4xl font-bold text-foreground">Recipify AI</h1>
          <p className="text-lg text-muted-foreground">
            Generate a recipe from the ingredients in your fridge
          </p>
        </div>

        {mutation.isPending && <LoadingOverlay message={loadingMessage} />}

        {mutation.isError && (
          <Card className="mb-4 p-4">
            <div className="text-destructive">Failed to generate a recipe. Please try again.</div>
            <div className="mt-2 text-sm text-muted-foreground">
              If this error persists, please contact{" "}
              <a
                href={`mailto:${supportEmail}?subject=${encodeURIComponent("Recipify Error Report")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                {supportEmail}
              </a>
            </div>
          </Card>
        )}

        <AuthModal
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={() => setShowAuthModal(false)}
        />

        {mutation.data ? (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">Generated Recipe</h3>
              <RecipeResult data={mutation.data} onBack={() => mutation.reset()} />
            </Card>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => mutation.reset()}>
                Back to form
              </Button>
              <Button
                type="button"
                onClick={submitRecipe}
                className="ml-auto"
                disabled={mutation.isPending}
              >
                Generate Again
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="p-6">
                <CategorySection
                  title="Meal Intent"
                  category="intent"
                  color="primary"
                  large
                  selectedValues={selected.intent}
                  onToggle={(option) => toggleSelection("intent", option)}
                  catalog={catalog}
                />
              </Card>

              <Card className="p-6">
                <h3 className="mb-4 text-lg font-semibold text-foreground">Servings</h3>
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setPortions((n) => Math.max(1, n - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-2xl font-bold text-foreground">{selected.portions}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setPortions((n) => Math.min(20, n + 1))}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            </div>

            <Card className="p-6">
              <h3 className="mb-6 text-xl font-semibold text-foreground">Available Ingredients</h3>
              <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <CategorySection
                    title="Protein"
                    category="protein"
                    color="protein"
                    selectedValues={selected.ingredients.protein}
                    onToggle={(option) => toggleIngredient("protein", option)}
                    catalog={catalog}
                  />
                  <CategorySection
                    title="Vegetables"
                    category="vegetables"
                    color="vegetable"
                    selectedValues={selected.ingredients.vegetables}
                    onToggle={(option) => toggleIngredient("vegetables", option)}
                    catalog={catalog}
                  />
                </div>

                <div className="border-t border-dashed border-border" />

                <div className="grid gap-6 md:grid-cols-2">
                  <CategorySection
                    title="Sides"
                    category="sides"
                    color="side"
                    selectedValues={selected.ingredients.sides}
                    onToggle={(option) => toggleIngredient("sides", option)}
                    catalog={catalog}
                  />
                  <CategorySection
                    title="Spices"
                    category="spices"
                    color="spice"
                    selectedValues={selected.spices}
                    onToggle={(option) => toggleSelection("spices", option)}
                    catalog={catalog}
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <CategorySection
                title="Available Appliances"
                category="appliance"
                color="appliance"
                large
                selectedValues={selected.appliance}
                onToggle={(option) => toggleSelection("appliance", option)}
                catalog={catalog}
              />
            </Card>

            <Card className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">Personal Preferences</h3>
              <Textarea
                value={selected.personal_preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder="e.g., I like Italian dishes, can get more spices if needed..."
                className="min-h-[100px]"
              />
            </Card>

            <Button type="submit" size="lg" className="w-full">
              Generate Recipe
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
